import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { saveAs } from 'file-saver';

/**
 * A single documentation record of a solution version.
 * Guides are markdown written in-app (content lives in the DB and is only
 * returned by the /view/ endpoint); documents are uploaded files
 * (.pdf / .xlsx / .docx) downloadable via their /file/ endpoint.
 */
export interface VersionDoc {
  id: string;
  title: string;
  doc_type: 'guide' | 'document' | string;
  description?: string;
  tenant_names?: string[] | null;
  file_name?: string | null;
  size?: number | null;
  updated_dtm?: string;
  url?: string;
  view_url?: string;
  /** Only present on the /view/ endpoint response. */
  content?: string;
}

/** One entry of GET /solution_app/documentation/ (docs the user can see). */
export interface VersionDocumentation {
  solution_id: string;
  solution_name: string;
  version_id: string;
  version_name: string;
  guides: VersionDoc[];
  documents: VersionDoc[];
}

@Injectable({
  providedIn: 'root',
})
export class DocumentationService {
  constructor(private http: HttpClient) {}

  /** Documentation of every version visible to the current user. */
  getMyDocumentation(): Observable<any> {
    const url = '/solution_app/documentation/';
    return this.http.get(url);
  }

  /** Producer view: all docs of one solution version. */
  getVersionDocs(solutionId: string, versionId: string): Observable<any> {
    const url = `/solution_app/${solutionId}/${versionId}/documentation/`;
    return this.http.get(url);
  }

  createGuide(
    solutionId: string,
    versionId: string,
    payload: {
      title: string;
      content: string;
      description?: string;
      tenant_names?: string[] | null;
    }
  ): Observable<any> {
    const url = `/solution_app/${solutionId}/${versionId}/documentation/`;
    return this.http.post(url, payload);
  }

  updateGuide(
    solutionId: string,
    versionId: string,
    docId: string,
    payload: {
      title?: string;
      content?: string;
      description?: string;
      tenant_names?: string[] | null;
    }
  ): Observable<any> {
    const url = `/solution_app/${solutionId}/${versionId}/documentation/${docId}/`;
    return this.http.put(url, payload);
  }

  /** Upload downloadable documents (multipart, file0..fileN). */
  uploadVersionDocs(
    solutionId: string,
    versionId: string,
    files: File[],
    tenantNames?: string[] | null
  ): Observable<any> {
    const url = `/solution_app/${solutionId}/${versionId}/documentation/`;

    const formData = new FormData();
    if (tenantNames && tenantNames.length) {
      formData.append('tenant_names', JSON.stringify(tenantNames));
    }
    files.forEach((file, index) => formData.append(`file${index}`, file));

    return this.http.post(url, formData);
  }

  deleteVersionDoc(
    solutionId: string,
    versionId: string,
    docId: string
  ): Observable<any> {
    const url = `/solution_app/${solutionId}/${versionId}/documentation/${docId}/`;
    return this.http.delete(url);
  }

  /** Fetch a single doc INCLUDING its markdown `content` via its view_url. */
  getDoc(viewUrl: string): Observable<any> {
    return this.http.get(viewUrl);
  }

  /**
   * Download a document's file (blob) and save it. Callers subscribe to the
   * returned observable to surface errors (blob errors: see parseError()).
   */
  downloadDoc(url: string, fileName: string): Observable<Blob> {
    return this.http
      .get(url, { responseType: 'blob' })
      .pipe(tap((blob: Blob) => saveAs(blob, fileName)));
  }

  /**
   * Extract a display message from an HttpErrorResponse. Handles blob error
   * bodies (from responseType: 'blob' requests) which must be text()-parsed.
   */
  async parseError(error: any, fallback = 'Something went wrong'): Promise<string> {
    try {
      let body = error?.error;
      if (body instanceof Blob) {
        body = JSON.parse(await body.text());
      }
      const msg = body?.msg ?? body?.error ?? body;
      if (typeof msg === 'string' && msg.trim()) return msg;
      if (msg && typeof msg === 'object') return JSON.stringify(msg);
    } catch {
      /* fall through to fallback */
    }
    return error?.message || fallback;
  }
}
